import React, { useEffect } from 'react'
import {X, Calendar, Gauge, Fuel, Car, Shield, ChevronRight} from 'lucide-react'

export interface VehicleSpec {
  fullName: string
  image: string
  price: string
  year: string
  mileage: string
  fuel: string
  power: string
  transmission: string
  badge: string
  badgeColor: string
  link: string
  name?: string
  // Fiche technique
  specs?: {
    color?: string
    fiscalPower?: string
    doors?: string
    firstRegistration?: string
    warranty?: string
    seats?: string
    length?: string
    weight?: string
    co2?: string
    description?: string
    equipments?: {
      category: string
      items: string[]
    }[]
    packs?: string[]
  }
}

interface VehicleModalProps {
  vehicle: VehicleSpec | null
  isOpen: boolean
  onClose: () => void
}

export default function VehicleModal({ vehicle, isOpen, onClose }: VehicleModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  if (!isOpen || !vehicle) return null

  const hasSpecs = vehicle.specs != null

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-300" />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-4xl mx-4 my-8 sm:my-12 animate-in fade-in slide-in-from-bottom-8 duration-500"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-zinc-900 border border-white/10 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl shadow-black/50">
          
          {/* Header image */}
          <div className="relative h-64 sm:h-80 md:h-96 overflow-hidden">
            <img 
              src={vehicle.image} 
              alt={vehicle.fullName || vehicle.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/30 to-transparent" />
            
            {/* Close button */}
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm border border-white/20 rounded-full p-2.5 text-white hover:bg-white/20 transition-all hover:scale-110 z-10"
              aria-label="Fermer"
            >
              <X size={22} />
            </button>

            {/* Badge */}
            <div className={`absolute top-4 left-4 bg-gradient-to-r ${vehicle.badgeColor} px-4 py-2 rounded-full text-white text-xs font-bold shadow-lg`}>
              {vehicle.badge}
            </div>

            {/* Name & Price overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                {vehicle.fullName || vehicle.name}
              </h2>
              <div className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-500" style={{ fontFamily: 'Syne, sans-serif' }}>
                {vehicle.price}
              </div>
            </div>
          </div>

          {/* Quick specs bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5">
            <div className="bg-zinc-900 p-4 sm:p-5 text-center">
              <Calendar size={20} className="text-blue-400 mx-auto mb-2" />
              <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Annee</p>
              <p className="text-white font-bold text-sm sm:text-base" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.year}</p>
            </div>
            <div className="bg-zinc-900 p-4 sm:p-5 text-center">
              <Gauge size={20} className="text-white mx-auto mb-2" />
              <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Kilometrage</p>
              <p className="text-white font-bold text-sm sm:text-base" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.mileage}</p>
            </div>
            <div className="bg-zinc-900 p-4 sm:p-5 text-center">
              <Fuel size={20} className="text-red-400 mx-auto mb-2" />
              <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Carburant</p>
              <p className="text-white font-bold text-sm sm:text-base" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.fuel}</p>
            </div>
            <div className="bg-zinc-900 p-4 sm:p-5 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400 mx-auto mb-2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Puissance</p>
              <p className="text-white font-bold text-sm sm:text-base" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.power}</p>
            </div>
          </div>

          {/* Detailed specs */}
          {hasSpecs && vehicle.specs ? (
            <div className="p-6 sm:p-8 space-y-8">
              
              {/* Key info cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {vehicle.specs.color && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Couleur</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.color}</p>
                  </div>
                )}
                {vehicle.specs.fiscalPower && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Puissance fiscale</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.fiscalPower}</p>
                  </div>
                )}
                {vehicle.specs.doors && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Portes</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.doors}</p>
                  </div>
                )}
                {vehicle.specs.firstRegistration && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>1ere mise en circulation</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.firstRegistration}</p>
                  </div>
                )}
                {vehicle.specs.warranty && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Garantie</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.warranty}</p>
                  </div>
                )}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Transmission</p>
                  <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.transmission}</p>
                </div>
                {vehicle.specs.seats && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Places</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.seats}</p>
                  </div>
                )}
                {vehicle.specs.length && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Longueur</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.length}</p>
                  </div>
                )}
                {vehicle.specs.weight && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Poids en charge</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.weight}</p>
                  </div>
                )}
                {vehicle.specs.co2 && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Emission CO2</p>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.specs.co2}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {vehicle.specs.description && (
                <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-5 sm:p-6">
                  <p className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>{vehicle.specs.description}</p>
                </div>
              )}

              {/* Equipment categories */}
              {vehicle.specs.equipments && vehicle.specs.equipments.length > 0 && (
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-white mb-6 flex items-center gap-3" style={{ fontFamily: 'Syne, sans-serif' }}>
                    <Car size={24} className="text-blue-400" />
                    Options & Equipements
                  </h3>
                  <div className="space-y-4">
                    {vehicle.specs.equipments.map((cat, i) => (
                      <EquipmentCategory key={i} category={cat.category} items={cat.items} />
                    ))}
                  </div>
                </div>
              )}

              {/* Packs */}
              {vehicle.specs.packs && vehicle.specs.packs.length > 0 && (
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Syne, sans-serif' }}>
                    <Shield size={24} className="text-blue-400" />
                    Packs & Options supplementaires
                  </h3>
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-400/20 rounded-2xl p-5 sm:p-6">
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      {vehicle.specs.packs.map((pack, i) => (
                        <div key={i} className="flex items-start gap-2.5 text-white/80">
                          <ChevronRight size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{pack}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                {vehicle.link ? (
                  <a 
                    href={vehicle.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white py-4 rounded-full font-bold text-base text-center hover:from-blue-500 hover:to-blue-400 transition-all hover:scale-105 shadow-lg"
                  >
                    Voir l'annonce
                  </a>
                ) : null}
                <a 
                  href="#contact" 
                  onClick={onClose}
                  className="flex-1 bg-white/10 border border-white/20 text-white py-4 rounded-full font-bold text-base text-center hover:bg-white/20 hover:border-blue-400/50 transition-all hover:scale-105"
                >
                  Nous contacter
                </a>
              </div>
            </div>
          ) : (
            /* No detailed specs - simple CTA */
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Transmission</p>
                  <p className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{vehicle.transmission}</p>
                </div>
              </div>
              <p className="text-white/50 text-center mb-6 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                La fiche technique detaillee sera bientot disponible. Contactez-nous pour plus d'informations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {vehicle.link ? (
                  <a 
                    href={vehicle.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white py-4 rounded-full font-bold text-base text-center hover:from-blue-500 hover:to-blue-400 transition-all hover:scale-105 shadow-lg"
                  >
                    Voir l'annonce
                  </a>
                ) : null}
                <a 
                  href="#contact" 
                  onClick={onClose}
                  className="flex-1 bg-white/10 border border-white/20 text-white py-4 rounded-full font-bold text-base text-center hover:bg-white/20 hover:border-blue-400/50 transition-all hover:scale-105"
                >
                  Nous contacter
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function EquipmentCategory({ category, items }: { category: string; items: string[] }) {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-white/5 transition-colors"
      >
        <span className="text-white font-bold text-sm sm:text-base" style={{ fontFamily: 'Syne, sans-serif' }}>{category}</span>
        <div className="flex items-center gap-2">
          <span className="text-white/40 text-xs" style={{ fontFamily: 'Outfit, sans-serif' }}>{items.length} elements</span>
          <ChevronRight 
            size={18} 
            className={`text-blue-400 transition-transform duration-300 ${isOpen ? 'rotate-90' : ''}`} 
          />
        </div>
      </button>
      {isOpen && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="grid sm:grid-cols-2 gap-1.5">
            {items.map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-white/70 py-1">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                <span className="text-xs sm:text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
